import React, { useState, useMemo } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Mountain, Target, Shield, Wind, Rocket, Check, HelpCircle, Info, RefreshCw, Plane, Gauge } from 'lucide-react';
import { Card } from '../ui/Card';
import { InputGroup } from '../ui/InputGroup';
import { TooltipIcon } from '../ui/TooltipIcon';
import { ActionCenter } from '../ui/ActionCenter';
import { MasterTipsCard } from '../ui/MasterTipsCard'; 
import { SectionHeader } from '../ui/SectionHeader';
import { formatCurrency, formatDate } from '../../utils/finance';
import { ASSET_COLORS } from '../../utils/constants';

const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const yearData = payload[0].payload.year; 
        return (
            <div className="bg-slate-900 border border-slate-700 p-3 rounded shadow-xl z-50">
                <p className="text-white font-bold mb-2 border-b border-slate-800 pb-1">{yearData} (Age {label})</p>
                {payload.map((p, i) => {
                    if (['goalHit', 'shortfall', 'age', 'year', 'goalId', 'goalName', 'netWorthNoBonus'].includes(p.name)) return null;
                    return <div key={i} className="flex items-center gap-2 text-xs mb-1" style={{ color: p.color }}><span>{p.name === 'netWorth' ? 'Base Reality' : p.name}:</span><span className="font-mono">{formatCurrency(p.value)}</span></div>;
                })}
            </div>
        );
    }
    return null;
};

export const DashboardTab = ({ data, actions, calculations, theme, setActiveTab, isVolatilityActive }) => {
    const [graphZoom, setGraphZoom] = useState('all');
    
    const safeCalcs = calculations || {};
    const { projections = [], currentNetWorth = 0, successRatio = 0, retirementData = {}, elementalHealth } = safeCalcs;

    const filteredProjections = useMemo(() => {
        if (graphZoom === 'all') return projections;
        const maxAge = (data.age || 0) + (graphZoom === '5y' ? 5 : 10);
        return projections.filter(p => p.age <= maxAge);
    }, [projections, graphZoom, data.age]);

    // Check if user has initialized their data
    const hasData = (data.assets && data.assets.length > 0) || currentNetWorth > 0;
    
    // Formatting the Last Review Date
    const lastSnapshotDate = data.lastVisionReview ? formatDate(data.lastVisionReview) : "Pending";

    // Progress to FI
    const fiNumber = retirementData?.fiNumber || 1;
    const progressPercent = Math.min(100, (currentNetWorth / fiNumber) * 100);

    return (
        <div className="animate-fade-in">
            
            {/* --- ACTION CENTER (THE BRAIN) --- */}
            {elementalHealth && (
                <ActionCenter 
                    health={elementalHealth} 
                    onNavigate={(tabId) => setActiveTab(tabId)} 
                />
            )}

            {/* --- MASTER TIPS CARD (Replaces Header & TabInfo) --- */}
            <MasterTipsCard 
                title="Your Financial Cockpit" 
                subtitle="Projections give you vision. The sky is the limit." 
                element="air"
            >
                This page is the result of all your other data. It aggregates your Assets (Earth), Income (Water), and Spending (Fire) 
                to project your long-term wealth trajectory. Use the <strong>Action Center</strong> above to see what needs your attention right now.
            </MasterTipsCard>

            <div className="grid grid-cols-1 gap-6">
                
                {/* 1. THE FLIGHT DECK (Top Analysis) */}
                <Card element="air" className="bg-gradient-to-r from-slate-900 to-cyan-900/30 border-cyan-500/20">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-6 gap-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-cyan-500/10 rounded-full border border-cyan-500/30">
                                <Plane className="w-8 h-8 text-cyan-400 -rotate-45" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Flight Status</h3>
                                <p className="text-sm text-slate-400">Current Trajectory vs. FI Target</p>
                            </div>
                        </div>
                        
                        <div className="text-right flex gap-8">
                            <div>
                                <span className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Altitude (Net Worth)</span>
                                <p className="text-2xl font-bold text-white">{formatCurrency(currentNetWorth)}</p>
                            </div>
                            <div>
                                <span className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Destination (FI)</span>
                                <p className="text-2xl font-bold text-cyan-400">{formatCurrency(fiNumber)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative">
                        <div className="flex justify-between text-[10px] uppercase font-bold text-slate-500 mb-1">
                            <span>Launch</span>
                            <span className={progressPercent >= 100 ? "text-emerald-400" : "text-cyan-400"}>{progressPercent.toFixed(1)}% to Freedom</span>
                        </div>
                        <div className="h-4 w-full bg-slate-950 rounded-full border border-slate-800 overflow-hidden relative">
                            <div 
                                className={`h-full transition-all duration-1000 ease-out relative ${progressPercent >= 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
                                style={{ width: `${progressPercent}%` }}
                            >
                                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')] opacity-20"></div>
                            </div>
                        </div>
                    </div>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    
                    {/* LEFT COLUMN: CHART (8 cols) */}
                    <div className="lg:col-span-8 h-[500px] bg-slate-900 rounded-xl border border-slate-800 p-4 relative overflow-hidden flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 relative z-10 gap-4">
                            
                            {/* Title & Logic Bubble */}
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold text-cyan-300 flex items-center gap-2"><Wind className="w-4 h-4"/> Vision Trajectory</h3>
                                <div className="group relative">
                                    <Info className="w-4 h-4 text-cyan-500/50 cursor-help hover:text-cyan-400" />
                                    <div className="absolute top-full left-0 mt-2 w-72 bg-slate-800 p-4 rounded-xl shadow-2xl border border-slate-700 hidden group-hover:block z-50 text-xs text-slate-300 leading-relaxed">
                                        <p className="font-bold text-white mb-2">How this projection works:</p>
                                        <p className="mb-2">This is a <strong>Flight Plan</strong>, not a live ticker.</p>
                                        <p className="mb-2">It takes a <strong>Snapshot</strong> of your assets right now, then applies your assumed growth rates and monthly contributions year over year.</p>
                                        <p className="italic text-cyan-400 border-l-2 border-cyan-500/30 pl-2">"Based on where you are standing today, this is where you will land."</p>
                                    </div>
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => actions.setSimulateVolatility(!isVolatilityActive)} 
                                    className={`text-[10px] px-3 py-1.5 rounded-lg border transition-all ${isVolatilityActive ? 'bg-cyan-500 text-slate-900 border-cyan-400 font-bold' : 'bg-transparent text-slate-500 border-slate-700 hover:text-white'}`}
                                >
                                    {isVolatilityActive ? '⚡ Simulation Active' : '○ Linear Model'}
                                </button>

                                <div className="flex items-center gap-2 pl-3 border-l border-slate-800">
                                    <div className="text-right hidden sm:block">
                                        <span className="block text-[9px] text-slate-500 uppercase tracking-widest">Snapshot</span>
                                        <span className="block text-[10px] font-mono text-cyan-400">{lastSnapshotDate}</span>
                                    </div>
                                    <button 
                                        onClick={actions.markVisionReviewed} 
                                        className="p-2 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors group"
                                        title="Confirm Snapshot is Current"
                                    >
                                        <RefreshCw className="w-3 h-3 group-hover:rotate-180 transition-transform" />
                                    </button>
                                </div>

                                <div className="flex gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800 ml-2">
                                    {['5y','10y','all'].map(z => <button key={z} onClick={() => setGraphZoom(z)} className={`text-[10px] px-2 py-1 rounded transition-colors ${graphZoom === z ? 'bg-cyan-900/50 text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}>{z.toUpperCase()}</button>)}
                                </div>
                            </div>
                        </div>

                        {/* Chart Area */}
                        <div className="flex-1 min-h-0 relative">
                        {!hasData ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 z-20">
                                <div className="bg-slate-950/80 p-8 rounded-2xl border border-slate-700 backdrop-blur-md max-w-md shadow-2xl flex flex-col items-center">
                                    <div className="bg-slate-800 p-4 rounded-full mb-4 animate-pulse">
                                        <Rocket className="w-8 h-8 text-cyan-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Ignite Your Vision</h3>
                                    <p className="text-slate-400 mb-6 text-sm">The projection engine needs fuel. Add your first asset to generate your trajectory.</p>
                                    <button 
                                        onClick={() => setActiveTab('allocation')} 
                                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold py-3 px-8 rounded-full transition-all hover:scale-105 shadow-lg shadow-emerald-500/20 flex items-center gap-2"
                                    >
                                        <Mountain className="w-4 h-4" /> Go to Assets
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={filteredProjections} margin={{top: 10, right: 10, left: 0, bottom: 0}}>
                                    <defs><linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={theme?.hex || '#22d3ee'} stopOpacity={0.3}/><stop offset="95%" stopColor={theme?.hex || '#22d3ee'} stopOpacity={0}/></linearGradient></defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                                    <XAxis dataKey="age" stroke="#475569" tick={{fontSize: 12}} tickMargin={10} />
                                    <YAxis stroke="#475569" tickFormatter={v => `$${v/1000}k`} tick={{fontSize: 12}} />
                                    <RechartsTooltip content={<CustomChartTooltip />} />
                                    {(data.assets || []).map((asset, index) => (
                                        <Area key={asset.id} type="monotone" dataKey={`asset_${asset.id}`} name={asset.name} stackId="1" stroke={ASSET_COLORS[index % ASSET_COLORS.length]} fill={ASSET_COLORS[index % ASSET_COLORS.length]} fillOpacity={0.6} />
                                    ))}
                                    <Line type="monotone" dataKey="netWorth" stroke="#ffffff" strokeWidth={3} dot={false} name="Net Worth" />
                                    <Line type="stepAfter" dataKey="fiNumber" stroke="#fbbf24" strokeDasharray="5 5" dot={false} name="FI Target" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN: FLIGHT CONTROLS (4 cols) */}
                    <div className="lg:col-span-4 space-y-4">
                        <Card element="air">
                            <SectionHeader icon={Gauge} title="Flight Controls" subtitle="Adjust your assumptions" element="air" />
                            
                            <div className="space-y-4">
                                <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                                    <InputGroup label="Current Age" value={data.age} onChange={(v) => actions.update('age', v)} element="air" />
                                    <InputGroup label="Retirement Age" value={data.retireAge} onChange={(v) => actions.update('retireAge', v)} element="air" />
                                </div>

                                <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                                    <InputGroup label="Base Salary" value={data.salary} onChange={(v) => actions.update('salary', v)} prefix="$" element="air" />
                                    <InputGroup label="Safe Withdrawal %" value={data.withdrawalRate || 4.0} onChange={(v) => actions.update('withdrawalRate', v)} suffix="%" element="air" />
                                </div>

                                <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-800/50">
                                    <div>
                                        <p className="text-[10px] uppercase text-slate-500 font-bold">Success Ratio</p>
                                        <p className={`text-xl font-bold ${successRatio >= 100 ? 'text-emerald-400' : 'text-orange-400'}`}>
                                            {(successRatio ?? 0).toFixed(0)}%
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-slate-500 uppercase font-bold">Years to FI</p>
                                        <p className="text-xl font-bold text-white">
                                            {Math.max(0, (data.retireAge - data.age))}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};